import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
    Activity, 
    ShieldCheck, 
    Save, 
    CheckSquare, 
    Square, 
    ChevronRight,
    Lock,
    Eye,
    PlusCircle,
    Edit3,
    Trash2,
    Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';
import GlassCard from '../components/UI/GlassCard';

const Permisos = () => {
    const { can } = useAuth();
    const [rolesMatriz, setRolesMatriz] = useState([]);
    const [modulos, setModulos] = useState([]);
    const [selectedRol, setSelectedRol] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const fetchMatriz = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/backend/api/permisos/index.php');
            if (response.data.success) {
                setRolesMatriz(response.data.data.roles);
                setModulos(response.data.data.modulos);
                // Seleccionar proimer rol por defecto si no hay uno
                if (!selectedRol && response.data.data.roles.length > 0) {
                    setSelectedRol(response.data.data.roles[0]);
                } else if (selectedRol) {
                    // Refrescar data del seleccionado actual
                    const updated = response.data.data.roles.find(r => r.rol_id === selectedRol.rol_id);
                    if (updated) setSelectedRol(updated);
                }
            }
        } catch (error) {
            toast.error('Error al cargar la malla de seguridad');
        } finally {
            setLoading(false);
        }
    }, [selectedRol]);

    useEffect(() => {
        fetchMatriz();
    }, []);

    useEffect(() => {
        if (!loading && selectedRol) {
            tippy('[data-tippy-content]', {
                animation: 'shift-away',
                theme: 'translucent',
                arrow: true
            });
        }
    }, [selectedRol, loading]);

    const handleToggle = (moduloName, key) => {
        if (!selectedRol) return;
        
        const newSelected = { ...selectedRol };
        const permisoIdx = newSelected.permisos.findIndex(p => p.modulo === moduloName);
        
        if (permisoIdx !== -1) {
            newSelected.permisos[permisoIdx][key] = !newSelected.permisos[permisoIdx][key];
            setSelectedRol(newSelected);
        }
    };

    const handleSave = async () => {
        if (!selectedRol) return;
        setSaving(true);
        try {
            const response = await axios.post('/backend/api/permisos/save.php', {
                rol_id: selectedRol.rol_id,
                permisos: selectedRol.permisos
            });
            if (response.data.success) {
                toast.success(`Jerarquía de ${selectedRol.nombre} blindada correctamente`);
                fetchMatriz();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Falla crítica en el guardado de privilegios');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen">
            {/* Header VIP */}
            <header className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <div className="inline-flex items-center gap-2 glass px-4 py-1.5 rounded-full border-white/5 mb-4 border-l-primary shadow-lg shadow-primary/10">
                        <Activity size={14} className="text-primary-light" />
                        <span className="text-[10px] font-black uppercase tracking-[2px] text-white/50">Matriz de Gobernanza</span>
                    </div>
                    <h1 className="text-5xl font-black text-white tracking-tighter">
                        Control de <span className="text-primary-light italic font-medium tracking-normal">Privilegios</span>
                    </h1>
                </div>
                {can('Permisos', 'editar') && (
                    <button 
                        onClick={handleSave}
                        disabled={saving || !selectedRol}
                        className={`group relative overflow-hidden bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-5 rounded-[22px] flex items-center gap-4 font-black uppercase tracking-[3px] text-xs transition-all shadow-2xl shadow-emerald-600/20 active:scale-95 disabled:opacity-50
                            ${saving ? 'animate-pulse' : ''}
                        `}
                    >
                        <Save size={18} className={saving ? 'animate-spin' : ''} />
                        {saving ? 'Blindando Matriz...' : 'Guardar Privilegios'}
                    </button>
                )}
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Selector de Roles (Horizontal en Tablet, Lateral en Desktop) */}
                <aside className="lg:col-span-1 flex lg:flex-col gap-3 overflow-x-auto lg:overflow-x-visible pb-6 lg:pb-0 custom-scrollbar -mx-4 px-4 lg:mx-0 lg:px-0 scroll-smooth">
                    {rolesMatriz.map((rol) => (
                        <button
                            key={rol.rol_id}
                            onClick={() => setSelectedRol(rol)}
                            className={`shrink-0 lg:w-full group relative flex items-center justify-between p-4 lg:p-6 rounded-2xl lg:rounded-[28px] border transition-all duration-500
                                ${selectedRol?.rol_id === rol.rol_id 
                                    ? 'bg-primary border-primary/20 shadow-2xl shadow-primary/30 -translate-y-1' 
                                    : 'glass border-white/5 hover:bg-white/5 text-white/50 hover:text-white'}
                            `}
                        >
                            <div className="flex items-center gap-3 lg:gap-4">
                                <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center font-black text-xs lg:text-base transition-colors ${selectedRol?.rol_id === rol.rol_id ? 'bg-white/20 text-white' : 'bg-white/5 text-white/20'}`}>
                                    {rol.nombre[0]}
                                </div>
                                <span className="font-bold tracking-tight text-sm lg:text-lg italic whitespace-nowrap">{rol.nombre}</span>
                            </div>
                            <ChevronRight size={18} className={`hidden lg:block transition-transform duration-500 ${selectedRol?.rol_id === rol.rol_id ? 'translate-x-2' : 'opacity-0'}`} />
                        </button>
                    ))}
                </aside>

                {/* Grid Maestro PHPRunner Style */}
                <main className="lg:col-span-3">
                    <GlassCard className="!p-0 border-white/5 bg-slate-950/40 min-h-[600px] overflow-hidden">
                        <div className="overflow-x-auto custom-scrollbar">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-white/[0.03] border-b border-white/5">
                                        <th className="px-6 lg:px-10 py-8 text-[10px] font-black uppercase tracking-[3px] text-white/30">Módulo del Sistema</th>
                                        <th className="px-8 py-8 text-center" data-tippy-content="Ver página y navegar">
                                            <div className="flex flex-col items-center gap-2">
                                                <Eye size={18} className="text-cyan-400 opacity-60" />
                                                <span className="text-[10px] font-black uppercase tracking-widest text-white/30">Acceso</span>
                                            </div>
                                        </th>
                                        <th className="px-8 py-8 text-center" data-tippy-content="Permiso para insertar nuevos datos">
                                            <div className="flex flex-col items-center gap-2">
                                                <PlusCircle size={18} className="text-emerald-400 opacity-60" />
                                                <span className="text-[10px] font-black uppercase tracking-widest text-white/30">Crear</span>
                                            </div>
                                        </th>
                                        <th className="px-8 py-8 text-center" data-tippy-content="Permiso para modificar registros">
                                            <div className="flex flex-col items-center gap-2">
                                                <Edit3 size={18} className="text-amber-400 opacity-60" />
                                                <span className="text-[10px] font-black uppercase tracking-widest text-white/30">Editar</span>
                                            </div>
                                        </th>
                                        <th className="px-8 py-8 text-center" data-tippy-content="Permiso para eliminar definitivamente">
                                            <div className="flex flex-col items-center gap-2">
                                                <Trash2 size={18} className="text-red-400 opacity-60" />
                                                <span className="text-[10px] font-black uppercase tracking-widest text-white/30">Borrar</span>
                                            </div>
                                        </th>
                                        <th className="px-8 py-8 text-center" data-tippy-content="Extraer a PDF/CSV/Email">
                                            <div className="flex flex-col items-center gap-2">
                                                <Download size={18} className="text-purple-400 opacity-60" />
                                                <span className="text-[10px] font-black uppercase tracking-widest text-white/30">Export</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6" className="p-24 text-center">
                                                <div className="flex flex-col items-center gap-4">
                                                    <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                                                    <p className="text-white/20 font-black uppercase tracking-widest text-xs">Cargando Mallas de Seguridad...</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : selectedRol?.permisos.map((p) => (
                                        <tr key={p.modulo} className="group hover:bg-white/[0.02] transition-colors">
                                            <td className="px-6 lg:px-10 py-6">
                                                <div className="flex items-center gap-3 lg:gap-4">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-primary/20 group-hover:bg-primary transition-colors"></div>
                                                    <span className="text-white font-bold text-sm lg:text-lg tracking-tight italic opacity-60 group-hover:opacity-100 transition-all whitespace-nowrap">{p.modulo}</span>
                                                </div>
                                            </td>
                                            
                                            {/* Columna Acceso */}
                                            <td className="px-8 py-6 text-center">
                                                <button 
                                                    onClick={() => can('Permisos', 'editar') && handleToggle(p.modulo, 'acceso')}
                                                    className={`p-3 rounded-2xl border transition-all ${!can('Permisos', 'editar') ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${p.acceso ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400 shadow-lg shadow-cyan-500/10' : 'bg-white/5 border-white/5 text-white/10'}`}
                                                >
                                                    {p.acceso ? <CheckSquare size={20} /> : <Square size={20} />}
                                                </button>
                                            </td>

                                            {/* Columna Crear */}
                                            <td className="px-8 py-6 text-center">
                                                <button 
                                                    onClick={() => can('Permisos', 'editar') && handleToggle(p.modulo, 'crear')}
                                                    className={`p-3 rounded-2xl border transition-all ${!can('Permisos', 'editar') ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${p.crear ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400 shadow-lg shadow-emerald-500/10' : 'bg-white/5 border-white/5 text-white/10'}`}
                                                >
                                                    {p.crear ? <CheckSquare size={20} /> : <Square size={20} />}
                                                </button>
                                            </td>

                                            {/* Columna Editar */}
                                            <td className="px-8 py-6 text-center">
                                                <button 
                                                    onClick={() => can('Permisos', 'editar') && handleToggle(p.modulo, 'editar')}
                                                    className={`p-3 rounded-2xl border transition-all ${!can('Permisos', 'editar') ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${p.editar ? 'bg-amber-500/20 border-amber-500/30 text-amber-400 shadow-lg shadow-amber-500/10' : 'bg-white/5 border-white/5 text-white/10'}`}
                                                >
                                                    {p.editar ? <CheckSquare size={20} /> : <Square size={20} />}
                                                </button>
                                            </td>

                                            {/* Columna Eliminar */}
                                            <td className="px-8 py-6 text-center">
                                                <button 
                                                    onClick={() => can('Permisos', 'editar') && handleToggle(p.modulo, 'eliminar')}
                                                    className={`p-3 rounded-2xl border transition-all ${!can('Permisos', 'editar') ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${p.eliminar ? 'bg-red-500/20 border-red-500/30 text-red-400 shadow-lg shadow-red-500/10' : 'bg-white/5 border-white/5 text-white/10'}`}
                                                >
                                                    {p.eliminar ? <CheckSquare size={20} /> : <Square size={20} />}
                                                </button>
                                            </td>

                                            {/* Columna Exportar */}
                                            <td className="px-8 py-6 text-center">
                                                <button 
                                                    onClick={() => can('Permisos', 'editar') && handleToggle(p.modulo, 'exportar')}
                                                    className={`p-3 rounded-2xl border transition-all ${!can('Permisos', 'editar') ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${p.exportar ? 'bg-purple-500/20 border-purple-500/30 text-purple-400 shadow-lg shadow-purple-500/10' : 'bg-white/5 border-white/5 text-white/10'}`}
                                                >
                                                    {p.exportar ? <CheckSquare size={20} /> : <Square size={20} />}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </GlassCard>
                </main>
            </div>
        </div>
    );
};

export default Permisos;
